import React, { useState } from 'react';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from '@mui/icons-material/WarningRounded';
import { ReactComponent as InfoIcon } from 'assets/images/criticalIncident/info_icon.svg';
import { ReactComponent as ErrorIcon } from 'assets/images/criticalIncident/exclamation_diamond.svg';
import { type Priority } from '../PriorityAlert';

import './ExpandableAlert.scss';

interface ExpandableAlertProps {
    summary: React.ReactNode;
    priority: Priority;
    children: React.ReactNode;
    defaultExpanded?: boolean;
    iconTooltip?: string;
}

const priorityConfig = {
    normal: {
        icon: <SvgIcon component={InfoIcon} inheritViewBox />,
        className: 'priority-normal',
    },
    high: {
        icon: <SvgIcon component={WarningIcon} inheritViewBox />,
        className: 'priority-high',
    },
    critical: {
        icon: <SvgIcon component={ErrorIcon} inheritViewBox />,
        className: 'priority-critical',
    },
} as const;

export const ExpandableAlert: React.FC<ExpandableAlertProps> = ({
    summary,
    priority,
    children,
    defaultExpanded = false,
    iconTooltip = '',
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const config = priorityConfig[priority];

    const icon = iconTooltip
        ? <span data-tooltip-right={iconTooltip}>{config.icon}</span>
        : config.icon;

    const handleToggle = () => setExpanded((prev) => !prev);

    return (
        <div className={`expandable-alert ${config.className}`}>
            <div
                className="expandable-alert__header"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={handleToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
            >
                <div className="expandable-alert__icon">{icon}</div>
                <div className="expandable-alert__summary">{summary}</div>
                <IconButton
                    className="expandable-alert__toggle"
                    size="small"
                    tabIndex={-1}
                    aria-hidden
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </div>
            <Collapse in={expanded}>
                <div className="expandable-alert__body">
                    {children}
                </div>
            </Collapse>
        </div>
    );
};
